import { WarningOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Input, message, Modal, Select, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useMemo, useState } from 'react';
import ReactJson from 'react-json-view';
import { useSelector } from 'react-redux';
import { FIVE_MINUTES, HTTPStatusCode } from '../../constants/CONSTANTS';
import { fetchAPI, HTTPError } from '../../utils/api';

export type MismapOrder = {
  order_id: number;
  po_reference_number: string | null;
  region: string | null;
  entity_name: string | null;
  buyer: {
    id: number;
    name: string;
    email: string;
    entity: { id: number; name: string } | null;
  } | null;
  // Mismatch ledger written by the reader:
  // {pdf: {...}, resolved: {...}, reasons: [...], created_at}
  // reasons entries are either a plain code or a {reason, ...detail} object.
  mismap: {
    pdf?: Record<string, unknown>;
    resolved?: Record<string, unknown>;
    reasons?: Array<string | Record<string, unknown>>;
    created_at?: string | null;
  } | null;
  is_tbd: boolean;
  created_at: string | null;
};

type EntityOption = { id: number; name: string };
type BuyerOption = { id: number; name: string; email: string };

const PER_PAGE = 20;
const ENTITY_PER_PAGE = 100;

const MismapRowFix = (props: {
  row: MismapOrder;
  entityOptions: EntityOption[];
  entitiesDisabled: boolean;
  onFixed: () => void;
}) => {
  const { row, entityOptions, entitiesDisabled, onFixed } = props;
  const [entityId, setEntityId] = useState<number | undefined>(
    row.buyer?.entity?.id
  );
  const [buyer, setBuyer] = useState<BuyerOption | undefined>(
    row.buyer
      ? { id: row.buyer.id, name: row.buyer.name, email: row.buyer.email }
      : undefined
  );
  const [buyerQ, setBuyerQ] = useState('');
  const [buyerOpen, setBuyerOpen] = useState(false);
  const [debouncedBuyerQ, setDebouncedBuyerQ] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBuyerQ(buyerQ), 300);
    return () => clearTimeout(timer);
  }, [buyerQ]);

  const entityBuyers = useQuery<{ items: BuyerOption[] }, HTTPError>(
    ['email-reader-mismap-entity-buyers', entityId, debouncedBuyerQ],
    async () => {
      if (!entityId) {
        return { items: [] };
      }
      const res = await fetchAPI(`entity/${entityId}/buyers`, {
        method: 'GET',
        payload: { page: 1, per_page: ENTITY_PER_PAGE, q: debouncedBuyerQ },
      });
      const data = res?.ok ?? res;
      return { items: data?.items ?? [] };
    },
    {
      enabled: !!entityId && buyerOpen,
      staleTime: FIVE_MINUTES,
      retry: (failureCount, error) =>
        error?.code !== HTTPStatusCode.FORBIDDEN && failureCount < 3,
    }
  );

  const buyerError = entityBuyers.error;
  const buyersForbidden = buyerError?.code === HTTPStatusCode.FORBIDDEN;

  const buyerList = useMemo(() => {
    const list: BuyerOption[] = [...(entityBuyers.data?.items ?? [])];
    // keep the selected buyer listed even when a later search drops it
    if (buyer && !list.some(b => b.id === buyer.id)) {
      list.push(buyer);
    }
    return list;
  }, [entityBuyers.data, buyer]);

  const save = async () => {
    if (!buyer) {
      return;
    }
    setSaving(true);
    try {
      await fetchAPI('email_reader/mismap/fix', {
        method: 'POST',
        payload: { order_id: row.order_id, entity_buyer_id: buyer.id },
      });
      message.success('Order map fixed');
      onFixed();
    } catch (error) {
      message.error(error?.message || 'Failed to fix order map');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Select
          showSearch
          allowClear
          value={entityId}
          placeholder="Select Entity"
          style={{ width: 200 }}
          disabled={entitiesDisabled}
          optionFilterProp="label"
          options={entityOptions.map(e => ({ label: e.name, value: e.id }))}
          onChange={(val: number | undefined) => {
            setEntityId(val);
            setBuyer(undefined);
          }}
        />
        <Select
          showSearch
          allowClear
          value={buyer?.id}
          disabled={!entityId || buyersForbidden || entitiesDisabled}
          loading={entityBuyers.isLoading}
          placeholder="Select Entity Buyer"
          style={{ width: 220 }}
          filterOption={false}
          onDropdownVisibleChange={setBuyerOpen}
          onSearch={setBuyerQ}
          onChange={(val: number | undefined) =>
            setBuyer(buyerList.find(b => b.id === val))
          }
          options={buyerList.map(b => ({
            label: `${b.name} (${b.email})`,
            value: b.id,
          }))}
        />
        <Button
          type="primary"
          size="small"
          loading={saving}
          disabled={!buyer}
          onClick={save}
        >
          Save Fix
        </Button>
      </div>
      {buyerError && (
        <div style={{ color: '#cf1322', fontSize: 12, marginTop: 4 }}>
          {`Buyer list unavailable: ${buyerError.message || 'forbidden'}`}
        </div>
      )}
    </div>
  );
};

const OrderModalMismapOrder = () => {
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [region, setRegion] = useState<string | undefined>(undefined);
  const user = useSelector((state: AppState) => state.user);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  const entities = useQuery<{ items: EntityOption[] }, HTTPError>(
    ['email-reader-mismap-entities', visible],
    async () => {
      if (!visible) {
        return { items: [] };
      }
      const getEntities = async (
        pageNumber = 1,
        prevs: EntityOption[] = []
      ): Promise<EntityOption[]> => {
        const res = await fetchAPI('entity/list-for-filter', {
          method: 'POST',
          payload: { page: pageNumber, per_page: ENTITY_PER_PAGE },
        });
        const data = res?.ok ?? res;
        const all = [...prevs, ...(data?.items ?? [])];
        const total = data?.total ?? 0;
        return total > pageNumber * ENTITY_PER_PAGE
          ? getEntities(pageNumber + 1, all)
          : all;
      };
      const items = await getEntities();
      return {
        items: items.sort((a, b) => a.name.localeCompare(b.name)),
      };
    },
    {
      enabled: visible,
      staleTime: FIVE_MINUTES,
      placeholderData: { items: [] },
      retry: (failureCount, error) =>
        error?.code !== HTTPStatusCode.FORBIDDEN && failureCount < 3,
    }
  );

  const entityError = entities.error;
  const entitiesForbidden = entityError?.code === HTTPStatusCode.FORBIDDEN;

  const mismapOrders = useQuery<
    { items: MismapOrder[]; total: number },
    HTTPError
  >(
    ['email-reader-mismap-list', page, debouncedQ, region, visible],
    async () => {
      if (!visible) {
        return { items: [], total: 0 };
      }
      const res = await fetchAPI('email_reader/mismap/list', {
        method: 'POST',
        payload: {
          page,
          per_page: PER_PAGE,
          q: debouncedQ,
          region: region || undefined,
        },
      });
      const data = res?.ok ?? res;
      return { items: data?.items ?? [], total: data?.total ?? 0 };
    },
    {
      staleTime: FIVE_MINUTES,
      placeholderData: { items: [], total: 0 },
      retry: (failureCount, error) =>
        error?.code !== HTTPStatusCode.FORBIDDEN && failureCount < 3,
    }
  );

  const listError = mismapOrders.error;

  const listForbidden = listError?.code === HTTPStatusCode.FORBIDDEN;

  const columns: ColumnsType<MismapOrder> = [
    {
      title: 'SAID',
      key: 'order_id',
      dataIndex: 'order_id',
      fixed: 'left',
      width: 120,
    },
    {
      title: 'PO Reference',
      key: 'po_reference_number',
      dataIndex: 'po_reference_number',
      width: 200,
      render: (text: string | null) => text || '-',
    },
    {
      title: 'Region',
      key: 'region',
      dataIndex: 'region',
      width: 100,
      render: (text: string | null) => text || '-',
    },
    {
      title: 'Current Entity',
      key: 'current-entity',
      width: 200,
      render: (text, r) => r.buyer?.entity?.name || r.entity_name || '-',
    },
    {
      title: 'Current Buyer',
      key: 'current-buyer',
      width: 220,
      render: (text, r) => (
        <div>
          <div>{r.buyer?.name || '-'}</div>
          {r.buyer?.email && (
            <div style={{ color: '#999', fontSize: 12 }}>{r.buyer.email}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Reasons',
      key: 'reasons',
      width: 250,
      render: (text, r) => {
        const reasons = r.mismap?.reasons ?? [];
        if (!reasons.length) {
          return r.is_tbd ? <Tag color="warning">TBD buyer</Tag> : '-';
        }
        return reasons.map((entry, index) => {
          const code =
            typeof entry === 'string'
              ? entry
              : typeof entry.reason === 'string'
              ? entry.reason
              : 'unknown';
          return (
            <Tag key={`${code}-${index}`} title={JSON.stringify(entry)}>
              {code}
            </Tag>
          );
        });
      },
    },
    {
      title: 'Snapshot',
      key: 'snapshot',
      width: 250,
      render: (text, r) => (
        <ReactJson
          name="snapshot"
          style={{ marginTop: 10 }}
          src={r.mismap || r}
          displayDataTypes={false}
          indentWidth={2}
          collapsed
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 520,
      fixed: 'right',
      render: (text, r) => (
        <MismapRowFix
          row={r}
          entityOptions={entities.data?.items ?? []}
          entitiesDisabled={!!entityError}
          onFixed={() => mismapOrders.refetch()}
        />
      ),
    },
  ];

  return (
    <>
      <Button icon={<WarningOutlined />} onClick={() => setVisible(true)}>
        Mismap Order
      </Button>
      <Modal
        title="Mismap Orders"
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={1200}
      >
        {entityError && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Entity list unavailable: ${entityError.message ||
              'forbidden'}`}
            description={
              entitiesForbidden
                ? 'Buyer directory access is required to assign an entity or buyer.'
                : undefined
            }
          />
        )}
        {listError && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Mismap list unavailable: ${listError.message ||
              'forbidden'}`}
            description={
              listForbidden
                ? 'Orders access is required to view mismapped orders.'
                : undefined
            }
          />
        )}
        <div style={{ display: 'flex', gap: 15, marginBottom: 15 }}>
          <Input.Search
            allowClear
            placeholder="Search PO Reference / Entity"
            style={{ maxWidth: 320 }}
            onChange={e => setQ(e.target.value)}
          />
          <Select
            allowClear
            placeholder="Region"
            style={{ width: 150 }}
            value={region}
            onChange={(val: string | undefined) => {
              setRegion(val);
              setPage(1);
            }}
            options={(user?.regions || []).map(regi => ({
              label: regi.trim(),
              value: regi.trim(),
            }))}
          />
        </div>
        <Table
          rowKey="order_id"
          dataSource={mismapOrders.data?.items ?? []}
          loading={mismapOrders.isLoading}
          columns={columns}
          pagination={{
            size: 'small',
            showSizeChanger: false,
            current: page,
            pageSize: PER_PAGE,
            total: mismapOrders.data?.total ?? 0,
            onChange: p => setPage(p),
            style: { marginBottom: -8 },
          }}
          scroll={{ x: 1750 }}
        />
      </Modal>
    </>
  );
};

export default OrderModalMismapOrder;
